import { useTranslation } from 'react-i18next';
import { Link, useLocation } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import { Globe, Moon, Sun } from 'lucide-react';
import { useTheme } from '@/components/theme/theme-provider';
import { useAppDispatch, useAppSelector } from '@/app/hooks';
import { persistor } from '@/app/store';
import { logoutSuccess } from '@/features/auth/authSlice';
import * as authService from '@/services/authService';
import { useNavigate } from 'react-router-dom';
import Logo from '@/assets/images/tulasi-circle.png';
import LogoWhite from '@/assets/images/tulasi-circle_white.png';

const languages = [
  { code: 'en', name: 'English' },
  { code: 'uk', name: 'Українська' },
];

const Header = () => {
  const { t, i18n } = useTranslation();
  const { theme, setTheme } = useTheme();
  const dispatch = useAppDispatch();
  const navigate = useNavigate();
  const isAuthenticated = useAppSelector((state) => state.auth.isAuthenticated);
  const user = useAppSelector((state) => state.auth.user);
  const location = useLocation();

  const handleLogout = async () => {
    try {
      await authService.logout();
      dispatch(logoutSuccess());
      persistor.purge();
      navigate('/');
    } catch (error) {
      console.error('Failed to logout:', error);
    }
  };

  const changeLanguage = (lng: string) => {
    i18n.changeLanguage(lng);
  };

  return (
    <header className="bg-background shadow-sm">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="flex justify-between items-center py-4">
          <div className="flex-shrink-0">
            <Link to="/">
              <img 
                src={theme === 'dark' ? LogoWhite : Logo} 
                alt="Print & Artisan Production Studio" 
                className="h-16 w-16 object-contain" 
              />
            </Link>
          </div>
          <nav className="hidden md:flex space-x-8">
            {location.pathname !== '/' && (
              <Link
                to="/"
                className="text-base font-medium text-muted-foreground hover:text-foreground"
              >
                {t('nav.home')}
              </Link>
            )}
            {location.pathname !== '/studio' && (
              <Link
                to="/studio"
                className="text-base font-medium text-muted-foreground hover:text-foreground"
              >
                {t('nav.studio', 'Studio')}
              </Link>
            )}
          </nav>
          <div className="flex items-center space-x-4">
            {isAuthenticated && (
              <span className="text-sm font-medium text-muted-foreground">
                {user?.name || user?.email}
              </span>
            )}
            <Button variant="ghost" onClick={handleLogout}>
              {t('nav.logout', 'Logout')}
            </Button>
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <Button variant="ghost" size="icon">
                  <Sun className="h-[1.2rem] w-[1.2rem] rotate-0 scale-100 transition-all dark:-rotate-90 dark:scale-0" />
                  <Moon className="absolute h-[1.2rem] w-[1.2rem] rotate-90 scale-0 transition-all dark:rotate-0 dark:scale-100" />
                  <span className="sr-only">Toggle theme</span>
                </Button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end">
                <DropdownMenuItem onClick={() => setTheme('light')}>
                  Light
                </DropdownMenuItem>
                <DropdownMenuItem onClick={() => setTheme('dark')}>
                  Dark
                </DropdownMenuItem>
                <DropdownMenuItem onClick={() => setTheme('system')}>
                  System
                </DropdownMenuItem>
              </DropdownMenuContent>
            </DropdownMenu>
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <Button variant="ghost" size="icon">
                  <Globe className="h-[1.2rem] w-[1.2rem]" />
                  <span className="sr-only">Change language</span>
                </Button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end">
                {languages.map((lang) => (
                  <DropdownMenuItem
                    key={lang.code}
                    onClick={() => changeLanguage(lang.code)}
                    disabled={i18n.language === lang.code}
                  >
                    {lang.name}
                  </DropdownMenuItem>
                ))}
              </DropdownMenuContent>
            </DropdownMenu>
          </div>
        </div>
      </div>
    </header>
  );
};

export default Header;
